import React from 'react';
import { useSuggestedUnits } from '../../../core/api';
import { ProjectCard, ProjectCardData } from '../../landing/components/ProjectCard';
import { useTranslation } from '../../../core/hooks/useTranslation';

interface SuggestedUnitsProps {
    slug: string;
}

/**
 * Maps raw unit data from the suggested endpoint to the standardized ProjectCardData structure.
 */
function mapUnitToCardData(unit: any, t: (key: string, fallback?: string) => string): ProjectCardData {
    return {
        id: Number(unit.id),
        title: unit.title || '',
        location: unit.project?.title || unit.location || '',
        specs: {
            beds: unit.beds ?? 0,
            baths: unit.baths ?? 0,
            area: `${unit.area ?? 0} ${t('common.m2', 'm²')}`
        },
        price: `${unit.price} ${t('common.currency', 'EGP')}`,
        payment: unit.payment || t('common.installments', 'Installments'),
        status: unit.status || '',
        image: unit.image || '',
        constructionStatus: unit.constructionStatus || undefined,
        finishType: unit.finishType || undefined,
        deliveryDate: unit.deliveryDate || undefined,
    };
}

export const SuggestedUnits: React.FC<SuggestedUnitsProps> = ({ slug }) => {
    const { t } = useTranslation();
    const { data: units, isLoading } = useSuggestedUnits(slug);

    if (isLoading) return <div className="animate-pulse h-96 bg-slate-100 rounded-lg" />;
    if (!units || units.length === 0) return null;

    return (
        <section className="mt-16 border-t border-slate-200 pt-16">
            <h3 className="text-3xl font-display font-bold text-secondary mb-8">
                {t('unit.suggested', 'You May Also Like')}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {units.map((unit) => {
                    const projectData = mapUnitToCardData(unit, t);

                    return (
                        <div key={unit.id} className="w-full h-full">
                            {/* Mobile: Horizontal Layout */}
                            <div className="block md:hidden h-full">
                                <ProjectCard
                                    project={projectData}
                                    linkTo={`/units/${unit.slug || unit.id}`}
                                    layout="horizontal-mobile"
                                    type="Unit"
                                    className="min-w-[280px] h-full"
                                />
                            </div>
                            {/* Desktop: Standard Layout (Vertical) */}
                            <div className="hidden md:block h-full">
                                <ProjectCard
                                    project={projectData}
                                    linkTo={`/units/${unit.slug || unit.id}`}
                                    layout="standard"
                                    type="Unit"
                                    className="h-full"
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};
